import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { getFighters, getFights, trackFeatureUsage } from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Share2,
  Users,
  Swords,
  Trophy,
  Copy,
  Check,
  ExternalLink,
  Image,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// Social Card Preview Component
const SocialCardPreview = ({ type, data }) => {
  if (!data) return null;

  if (type === 'fighter') {
    return (
      <div className="bg-gradient-to-br from-ba-black via-zinc-900 to-ba-black border border-red-600/30 rounded-sm overflow-hidden">
        {/* Header */}
        <div className="bg-red-600 px-4 py-2 flex items-center justify-between">
          <span className="font-heading text-white text-sm tracking-wider">BLACK ARROW MMA</span>
          <span className="text-white/80 text-xs">{data.gym_name}</span>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-zinc-500" />
            </div>
            <div>
              <p className="font-heading text-2xl text-white">{data.fighter?.name}</p>
              {data.fighter?.nickname && (
                <p className="text-zinc-500">"{data.fighter.nickname}"</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-6 mb-4">
            <div>
              <p className="text-[10px] tracking-wider uppercase text-zinc-500">Record</p>
              <p className="font-heading text-xl text-white">{data.fighter?.record}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-wider uppercase text-zinc-500">Weight Class</p>
              <p className="font-heading text-xl text-white">{data.fighter?.weight_class}</p>
            </div>
          </div>
          
          {data.assessment && (
            <div className="grid grid-cols-4 gap-2 pt-4 border-t border-ba-border">
              <div className="text-center">
                <p className="font-heading text-lg text-white">{data.assessment.striking}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Striking</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-lg text-white">{data.assessment.grappling}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Grappling</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-lg text-white">{data.assessment.wrestling}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Wrestling</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-lg text-white">{data.assessment.cardio}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Cardio</p>
              </div>
            </div>
          )}
          
          {data.upcoming_fight && (
            <div className="mt-4 pt-4 border-t border-ba-border">
              <p className="text-[10px] tracking-wider uppercase text-red-500 mb-1">Next Fight</p>
              <p className="text-white">vs {data.upcoming_fight.opponent}</p>
              <p className="text-zinc-500 text-sm">{data.upcoming_fight.event} - {data.upcoming_fight.date}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (type === 'fight') {
    return (
      <div className="bg-gradient-to-br from-ba-black via-zinc-900 to-ba-black border border-red-600/30 rounded-sm overflow-hidden">
        {/* Header */}
        <div className="bg-red-600 px-4 py-2 flex items-center justify-between">
          <span className="font-heading text-white text-sm tracking-wider">FIGHT ANNOUNCEMENT</span>
          <span className="text-white/80 text-xs">{data.gym_name}</span>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <p className="text-[10px] tracking-wider uppercase text-zinc-500 mb-2">{data.fight?.event_name}</p>
          
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="text-center flex-1">
              <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-2">
                <Users className="w-8 h-8 text-zinc-500" />
              </div>
              <p className="font-heading text-xl text-white">{data.fighter?.name}</p>
              {data.fighter?.nickname && (
                <p className="text-zinc-500 text-sm">"{data.fighter.nickname}"</p>
              )}
              <p className="text-red-500 font-heading">{data.fighter?.record}</p>
            </div>
            
            <div className="text-center">
              <p className="font-heading text-3xl text-zinc-600">VS</p>
            </div>
            
            <div className="text-center flex-1">
              <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-2">
                <Users className="w-8 h-8 text-zinc-500" />
              </div>
              <p className="font-heading text-xl text-white">{data.fight?.opponent || 'TBA'}</p>
              <p className="text-zinc-500 text-sm">Opponent</p>
            </div>
          </div>
          
          <div className="text-center pt-4 border-t border-ba-border">
            <p className="font-heading text-lg text-white">{data.fight?.date}</p>
            <p className="text-zinc-500">{data.fight?.location || 'Location TBA'}</p>
            <p className="text-red-500 text-sm mt-2">{data.fight?.weight_class}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const SocialSharingPage = () => {
  const [fighters, setFighters] = useState([]);
  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareType, setShareType] = useState('fighter');
  const [selectedFighter, setSelectedFighter] = useState('');
  const [selectedFight, setSelectedFight] = useState('');
  const [cardData, setCardData] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [fightersRes, fightsRes] = await Promise.all([
        getFighters(),
        getFights()
      ]);
      setFighters(fightersRes.data);
      setFights(fightsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    trackFeatureUsage('social_sharing', 'view').catch(() => {});
  }, [loadData]);

  const loadSocialCard = async () => {
    if (shareType === 'fighter' && !selectedFighter) {
      toast.error('Please select a fighter');
      return;
    }
    if (shareType === 'fight' && !selectedFight) {
      toast.error('Please select a fight');
      return;
    }

    setLoadingCard(true);
    try {
      const endpoint = shareType === 'fighter' 
        ? `${API}/api/fighters/${selectedFighter}/social-card`
        : `${API}/api/fights/${selectedFight}/social-card`;
      
      const response = await axios.get(endpoint);
      setCardData(response.data);
      trackFeatureUsage('social_sharing', 'generate_card', { type: shareType }).catch(() => {});
    } catch (error) {
      toast.error('Failed to generate social card');
    } finally {
      setLoadingCard(false);
    }
  };

  const handleShare = (platform) => {
    let text = '';
    if (shareType === 'fighter' && cardData) {
      text = `Check out ${cardData.fighter?.name} (${cardData.fighter?.record}) from ${cardData.gym_name}! #MMA #BlackArrowMMA`;
    } else if (shareType === 'fight' && cardData) {
      text = `${cardData.fighter?.name} vs ${cardData.fight?.opponent || 'TBA'} at ${cardData.fight?.event_name}! ${cardData.fight?.date} #MMA #FightNight`;
    }

    const url = cardData?.share_url ? `${window.location.origin}${cardData.share_url}` : window.location.origin;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    
    trackFeatureUsage('social_sharing', 'share', { platform, type: shareType }).catch(() => {});
  };

  const handleCopyText = () => {
    let text = '';
    if (shareType === 'fighter' && cardData) {
      text = `${cardData.fighter?.name} (${cardData.fighter?.record}) - ${cardData.fighter?.weight_class}\n${cardData.gym_name}\n\n${cardData.share_url ? `${window.location.origin}${cardData.share_url}` : ''}`;
    } else if (shareType === 'fight' && cardData) {
      text = `${cardData.fighter?.name} vs ${cardData.fight?.opponent || 'TBA'}\n${cardData.fight?.event_name}\n${cardData.fight?.date}\n${cardData.fight?.location || 'Location TBA'}`;
    }
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="social-sharing-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Promote</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            SOCIAL SHARING
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Create and share content for social media</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="space-y-6">
            {/* Share Type Selection */}
            <div className="bg-ba-surface border border-ba-border rounded-sm p-6">
              <h2 className="font-heading text-lg text-white mb-4">What to Share</h2>
              
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  onClick={() => {
                    setShareType('fighter');
                    setCardData(null);
                  }}
                  className={cn(
                    "p-4 rounded-sm border transition-all flex flex-col items-center gap-2",
                    shareType === 'fighter' 
                      ? "bg-red-600/20 border-red-600/50 text-white"
                      : "border-ba-border text-zinc-400 hover:border-zinc-600"
                  )}
                >
                  <Users className="w-6 h-6" />
                  <span className="font-heading">Fighter Profile</span>
                </button>
                <button
                  onClick={() => {
                    setShareType('fight');
                    setCardData(null);
                  }}
                  className={cn(
                    "p-4 rounded-sm border transition-all flex flex-col items-center gap-2",
                    shareType === 'fight' 
                      ? "bg-red-600/20 border-red-600/50 text-white"
                      : "border-ba-border text-zinc-400 hover:border-zinc-600"
                  )}
                >
                  <Swords className="w-6 h-6" />
                  <span className="font-heading">Fight Announcement</span>
                </button>
              </div>

              {/* Selection */}
              {shareType === 'fighter' ? (
                <div className="space-y-4 relative z-50">
                  <Select value={selectedFighter} onValueChange={setSelectedFighter}>
                    <SelectTrigger className="h-12 bg-ba-black border-ba-border">
                      <SelectValue placeholder="Select a fighter..." />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {fighters.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name} - {f.weight_class}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-4 relative z-50">
                  <Select value={selectedFight} onValueChange={setSelectedFight}>
                    <SelectTrigger className="h-12 bg-ba-black border-ba-border">
                      <SelectValue placeholder="Select a fight..." />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {fights.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.opponent || 'TBA'} - {f.fight_date}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                onClick={loadSocialCard}
                disabled={loadingCard}
                className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
              >
                {loadingCard ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Image className="w-4 h-4 mr-2" />
                )}
                Generate Preview
              </Button>
            </div>

            {/* Share Options */}
            {cardData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-ba-surface border border-ba-border rounded-sm p-6"
              >
                <h2 className="font-heading text-lg text-white mb-4">Share To</h2>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleShare('twitter')}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X / Twitter
                  </Button>
                  
                  <Button
                    onClick={() => handleShare('facebook')}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </Button>
                  
                  <Button
                    onClick={() => handleShare('linkedin')}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </Button>
                  
                  <Button
                    onClick={handleCopyText}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 mr-2 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5 mr-2" />
                    )}
                    Copy Text
                  </Button>
                </div>

                {cardData.share_url && (
                  <div className="mt-4 pt-4 border-t border-ba-border">
                    <p className="text-zinc-500 text-sm mb-2">Public Profile Link:</p>
                    <div className="flex gap-2">
                      <code className="flex-1 bg-ba-black p-2 rounded text-xs text-zinc-400 overflow-x-auto">
                        {window.location.origin}{cardData.share_url}
                      </code>
                      <Button
                        size="icon"
                        variant="outline"
                        className="border-zinc-700 flex-shrink-0"
                        onClick={() => window.open(cardData.share_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Preview */}
          <div>
            <h2 className="font-heading text-lg text-white mb-4">Preview</h2>
            {cardData ? (
              <SocialCardPreview type={shareType} data={cardData} />
            ) : (
              <div className="bg-ba-surface border border-ba-border rounded-sm p-12 text-center">
                <Share2 className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500">Select a {shareType} and click "Generate Preview" to see the social card</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SocialSharingPage;
